import type { AxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import styled from "styled-components/macro";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import { Breadcrumb } from "../../../components/ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../../../components/LinkUnstyled/LinkUnstyled";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { ErrorPlaceholder } from "../../../components/Error";
import { Tab, Tabs } from "../../../components/Tabs/Tabs";
import {
  Text3xlBold,
  TextSmRegular,
  TextSmMedium,
} from "../../../components/Typography/TypographyV2";
import { endpoints } from "../../../endpoints";
import { ContentWrapper, PageWrapper } from "../../../layout/portalPageLayout";
import type { Tenant } from "../../../types/types";
import { useRoutePath } from "../../../util/Routing";
import { useAuthenticatedUser } from "../../../hooks/useAuthenticatedUser";
import { makeUrlWithParams, useStoreState } from "../../../util/util";
import { CompanyInfo } from "../../SharedPages/CustomerDetailPage/CompanyInfo/CompanyInfo";
import { CustomerSettings } from "../../SharedPages/CustomerDetailPage/CustomerSettings/CustomerSettings";
import { UsersList } from "../../SharedPages/CustomerDetailPage/UsersList/UsersList";
import { PriceTiersTab } from "../../SharedPages/CustomerDetailPage/PriceTiersTab/PriceTiersTab";

const PageTitle = styled(Text3xlBold).attrs({ as: "h1" })`
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
`;

const MetadataRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  margin: ${({ theme }) => theme.resolve("spacing_xs")} 0
    ${({ theme }) => theme.resolve("spacing_lg")};
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xxs")};
`;

export const DistributorDetailPage = () => {
  const user = useAuthenticatedUser();
  const {
    storefront_id,
    storefront_metadata: { edition },
  } = useStoreState();
  const { distributor_id: distributorTenantId } =
    useParams<{ distributor_id: string }>();
  const [query] = useQueryParams({
    q: StringParam,
    offset: NumberParam,
    perPage: NumberParam,
  });
  const { adminPath } = useRoutePath();
  const { t } = useTranslation();

  const {
    data: distributorTenant,
    error: distributorTenantError,
    mutate: mutateBuyerTenant,
  } = useSWR<Tenant, AxiosError>(
    distributorTenantId
      ? makeUrlWithParams(
          endpoints.v1_storefronts_id_tenants_id(
            storefront_id,
            distributorTenantId
          ),
          {
            show_inactive_address: "true",
          }
        )
      : null
  );

  if (distributorTenantError) {
    return <ErrorPlaceholder message={t("Failed to fetch customer.")} />;
  }
  if (!distributorTenant) {
    // Loading.
    return <DelayedSpinner />;
  }

  return (
    <PageWrapper narrow>
      <Breadcrumb>
        <LinkUnstyled
          to={makeUrlWithParams(`${adminPath}/distributors`, {
            q: query.q,
            offset: query.offset,
            perPage: query.perPage,
          })}
        >
          {t("Distributors")}
        </LinkUnstyled>
        <div>/</div>
        <div>{distributorTenant.name}</div>
      </Breadcrumb>
      <PageTitle>{distributorTenant.name}</PageTitle>
      {(distributorTenant.crm_id ||
        distributorTenant.external_identity?.external_id) && (
        <MetadataRow>
          {distributorTenant.crm_id && (
            <MetadataItem>
              <TextSmRegular color="secondary">{t("CRM ID")}:</TextSmRegular>
              <TextSmMedium>{distributorTenant.crm_id}</TextSmMedium>
            </MetadataItem>
          )}
          {distributorTenant.external_identity?.external_id && (
            <MetadataItem>
              <TextSmRegular color="secondary">
                {t("Distributor ID")}:
              </TextSmRegular>
              <TextSmMedium>
                {distributorTenant.external_identity?.external_id}
              </TextSmMedium>
            </MetadataItem>
          )}
        </MetadataRow>
      )}
      <ContentWrapper>
        <Tabs selectedIndex="Company Info">
          <Tab label={t("Company Info")}>
            <CompanyInfo
              buyerTenant={distributorTenant}
              mutateBuyerTenant={mutateBuyerTenant}
            />
          </Tab>
          <Tab label={t("Users")}>
            <UsersList
              tenantID={distributorTenant.id}
              userType="Distributor Admin"
            />
          </Tab>
          <Tab label={t("Settings")}>
            <CustomerSettings
              buyerTenant={distributorTenant}
              loggedInUser={user}
            />
          </Tab>
          {edition !== "pim" ? (
            <Tab label={t("Price Tiers")}>
              <PriceTiersTab buyerTenant={distributorTenant} user={user} />
            </Tab>
          ) : (
            <></>
          )}
        </Tabs>
      </ContentWrapper>
    </PageWrapper>
  );
};
